import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'
import { CurrencySchema } from '@shared/contracts/enums'

const MoneySchema = z.object({
  amount: z.string().regex(/^-?\d+\.\d{3}$/),
  currency: CurrencySchema,
})
export const CustomerSuccessSchema = z.object({
  schemaVersion: z.literal(1),
  ruleVersion: z.literal('cs-v2'),
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string().nullable(),
    createdAt: z.string(),
  }),
  window: z.object({
    days: z.number(),
    timeZone: z.string(),
    from: z.string(),
    to: z.string(),
    previousFrom: z.string(),
    previousTo: z.string(),
    asOf: z.string(),
  }),
  currency: CurrencySchema,
  availableCurrencies: z.array(CurrencySchema),
  gmv: z.object({ current: MoneySchema, previous: MoneySchema, paidCharges: z.number() }),
  revenueChange: z.object({
    status: z.enum(['AVAILABLE', 'NO_BASE', 'INSUFFICIENT_HISTORY']),
    percentage: z.number().nullable(),
    amount: MoneySchema,
  }),
  timeToFirstValue: z.object({
    status: z.enum(['REACHED', 'NOT_REACHED', 'INCONSISTENT_HISTORY']),
    firstPaidAt: z.string().nullable(),
    days: z.number().nullable(),
  }),
  adoption: z.object({
    percentage: z.number(),
    used: z.number(),
    total: z.number(),
    features: z.array(
      z.object({
        key: z.enum(['RECEIVING_VERIFIED', 'SALES_CHANNEL', 'CONFIRMED_SALE']),
        used: z.boolean(),
      }),
    ),
  }),
  lastActivity: z.object({
    at: z.string().nullable(),
    source: z.enum(['LOGIN', 'PAID_SALE']).nullable(),
    days: z.number().nullable(),
    loginObserved: z.boolean(),
  }),
  churnRisk: z.object({
    level: z.enum(['LOW', 'MEDIUM', 'HIGH', 'UNKNOWN']),
    reason: z.enum([
      'SALES_BLOCKED',
      'NO_FIRST_VALUE',
      'INACTIVE_30_DAYS',
      'INACTIVE_14_DAYS',
      'REVENUE_DROP_50',
      'REVENUE_DROP_20',
      'LOW_ADOPTION',
      'ONBOARDING',
      'INSUFFICIENT_ACTIVITY',
      'REGULAR_SIGNALS',
    ]),
    ruleVersion: z.literal('cs-v2'),
  }),
})
export type CustomerSuccessMetrics = z.infer<typeof CustomerSuccessSchema>

export function useCustomerSuccess(
  userId: string,
  days: number,
  currency: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['admin-customer-success', userId, days, currency],
    queryFn: async () =>
      CustomerSuccessSchema.parse(
        await apiFetch<unknown>(
          `/admin/users/${encodeURIComponent(userId)}/customer-success?${new URLSearchParams({ days: String(days), currency })}`,
        ),
      ),
    enabled: enabled && userId.length > 0,
  })
}
